/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from 'react';

import { Button, makeStyles, Typography } from '@material-ui/core';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { enqueueSnackbar } from 'notistack';

import { toolbar } from '../../constants/reportEditor';
import WorkspaceService from '../../services/workspace.service';
import { createEditorConfig, setupPasteHandler } from '../../utils/editorUtils';
import { sanitizeRichText, createSanitizedHtml } from '../../utils/sanitize';

const useStyles = makeStyles(() => ({
  '@global': {
    '.richtexteditor rte-powerby a': {
      cursor: 'default !important',
      opacity: '0 !important',
      pointerEvents: 'none !important',
      width: '0px !important',
    },
  },
  buttonsContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
  },
  editorContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    minHeight: '200px',
  },
  noSignature: {
    color: '#bbb',
  },
}));

const UserSignature = ({ user }) => {
  const [showSignatureEditor, setShowSignatureEditor] = useState(false);
  const [signatureEditor, setSignatureEditor] = useState(null);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [signatureContent, setSignatureContent] = useState('');

  const signatureEditorRef = useRef(null);
  const classes = useStyles();

  const fetchSignatureContent = async () => {
    if (!user?.id) return;

    try {
      const workspaceService = new WorkspaceService();
      const signature = await workspaceService.getUserSignature(user.id);

      if (signature?.content) {
        setSignatureContent(signature.content);
      }
    } catch (error) {
      console.error('Error fetching signature:', error);
    }
  };

  useEffect(() => {
    fetchSignatureContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    return () => {
      if (signatureEditor) {
        const parent = signatureEditorRef.current?.parentElement;
        if (parent) {
          parent.innerHTML = '<div ref="signatureEditorRef"></div>';
        }
        setSignatureEditor(null);
      }
    };
  }, [signatureEditor]);

  const openSignatureEditor = () => {
    setShowSignatureEditor(true);

    setTimeout(() => {
      const baseConfig = {
        editorResizeMode: 'height',
        enablecontextmenu: false,
        enableobjectresizing: true,
        enterKeyTag: 'div',
        maxImageSize: 2 * 1024 * 1024,
        maxTextLength: 1000,
        showFloatParagraph: false,
        showPlusButton: false,
        showPoweredBy: false,
        showTagList: false,
        toolbar: 'headerToolbar',
        toolbar_headerToolbar: `${toolbar}#{undo,redo}`,
        width: '100%',
      };

      const editorConfig = createEditorConfig(baseConfig);
      const editor = new window.RichTextEditor(signatureEditorRef.current, editorConfig);
      editor.setHTMLCode(signatureContent);
      setupPasteHandler(editor);
      setSignatureEditor(editor);
    }, 100);
  };

  const closeSignatureEditor = () => {
    if (signatureEditor) {
      const parent = signatureEditorRef.current?.parentElement;
      if (parent) {
        parent.innerHTML = '<div ref="signatureEditorRef"></div>';
      }
      setSignatureEditor(null);
    }

    setShowSignatureEditor(false);
  };

  const saveUserSignature = async () => {
    if (!user?.id) return;

    try {
      setIsSavingSignature(true);
      const rawContent = signatureEditor.getHTMLCode();
      const sanitizedContent = sanitizeRichText(rawContent);
      const workspaceService = new WorkspaceService();

      if (!user.signatureId) {
        await workspaceService.createUserSignature(user.id, sanitizedContent);
      } else {
        await workspaceService.updateUserSignature(user.id, sanitizedContent);
      }

      setSignatureContent(sanitizedContent);
      closeSignatureEditor();
    } catch (error) {
      console.error('Error saving user signature:', error);

      if (error.statusCode === 413 || error.message?.includes('request entity too large') || error.response?.status === 413) {
        enqueueSnackbar('Your signature exceeds the maximum allowed size. Please reduce the content or the size or number of images and try again.', {
          autoHideDuration: 8000,
          variant: 'error',
        });
      } else {
        enqueueSnackbar('An error occurred while saving your signature. Please try again later.', { autoHideDuration: 8000, variant: 'error' });
      }
    } finally {
      setIsSavingSignature(false);
    }
  };

  return (
    <div className="table-container">
      <Typography className="secondary-header mb-1">User&apos;s Report Signature</Typography>

      {showSignatureEditor ? (
        <div className={classes.editorContainer}>
          <div className="editor-container">
            <div ref={signatureEditorRef} style={{ minHeight: '200px' }} />
          </div>

          <div className={classes.buttonsContainer}>
            <Button variant="outlined" color="secondary" onClick={closeSignatureEditor}>
              Cancel
            </Button>

            <Button variant="contained" color="primary" onClick={saveUserSignature} disabled={isSavingSignature} startIcon={<SaveOutlinedIcon />} style={{ marginLeft: '8px' }}>
              {isSavingSignature ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {signatureContent ? (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={createSanitizedHtml(signatureContent)} />
          ) : (
            <Typography className={classes.noSignature}>No signature is set for your account</Typography>
          )}
          <Button variant="contained" color="primary" onClick={openSignatureEditor} startIcon={<EditNoteIcon />} className="mt-1">
            Edit Signature
          </Button>
        </>
      )}
    </div>
  );
};

export default UserSignature;
